import React from 'react';
import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { calendarEventCard } from '@/styles/tokens';
import { CalendarEventDto } from '@/types/calendar';
import { EventTypeIcon } from './EventTypeIcon';

/** `completed` / `missed` - смуга «Виконано» / «Невиконано» під карткою, `none` - без смуги. */
export type EventCardStatus = 'completed' | 'missed' | 'none';

interface CalendarEventStatusCardProps {
  event: CalendarEventDto;
  status: EventCardStatus;
  onClick: () => void;
}

const STATUS_LABEL: Record<Exclude<EventCardStatus, 'none'>, string> = {
  completed: 'Виконано',
  missed: 'Невиконано',
};

const RAW_INPUT_TYPES = ['food', 'workout', 'medication'];

const Card = styled(Box)(({ theme }) => {
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  return {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: calendarEventCard.bg[mode],
    borderRadius: 8,
    '& .status-card-body': {
      display: 'flex',
      gap: 4,
      padding: '6px 4px',
      border: `1px solid ${calendarEventCard.border[mode]}`,
      borderRadius: 8,
    },
    '& .status-card-body.with-status': {
      alignItems: 'center',
      borderBottomWidth: 0,
      borderRadius: '8px 8px 0 0',
    },
  };
});

/**
 * Картка події в сітці календаря клієнта (зона експерта): тип, назва, опис, аватарка автора і, для призначень
 * поточного експерта, смуга виконання. Без смуги опис переноситься на кілька рядків.
 */
export const CalendarEventStatusCard: React.FC<CalendarEventStatusCardProps> = ({ event, status, onClick }) => {
  const hasStatus = status !== 'none';
  const rawInput = RAW_INPUT_TYPES.includes(event.eventType.code)
    ? ((event.details as Record<string, unknown>)?.rawInput as string | undefined)
    : undefined;

  return (
    <Card onClick={onClick}>
      <Box className={hasStatus ? 'status-card-body with-status' : 'status-card-body'}>
        <EventTypeIcon
          code={event.eventType.code}
          icon={event.eventType.icon}
          accentColor={event.eventType.color}
          size={16}
          sx={{ flexShrink: 0, alignSelf: 'flex-start' }}
        />
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography noWrap sx={{ fontSize: 12, lineHeight: '14px', fontWeight: 600, color: 'text.primary' }}>
            {event.title || event.eventType.name}
          </Typography>
          {rawInput && (
            <Typography
              noWrap={hasStatus}
              sx={{ fontSize: 10, lineHeight: '12px', color: 'text.primary', overflowWrap: 'anywhere' }}
            >
              {rawInput}
            </Typography>
          )}
        </Box>
        {event.createdBy && (
          <Tooltip title={event.createdBy.name} placement="top">
            <Avatar
              src={event.createdBy.avatar ?? undefined}
              sx={{ width: 16, height: 16, fontSize: '0.55rem', flexShrink: 0, alignSelf: 'flex-start' }}
            >
              {!event.createdBy.avatar && event.createdBy.name.charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
        )}
      </Box>
      {status !== 'none' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            height: 20,
            p: '4px 4px 4px 8px',
            bgcolor: calendarEventCard[status],
            color: calendarEventCard.statusText,
          }}
        >
          {status === 'completed'
            ? <CheckCircleOutlineIcon sx={{ fontSize: 12 }} />
            : <HighlightOffIcon sx={{ fontSize: 12 }} />}
          <Typography noWrap sx={{ fontSize: 10, lineHeight: '12px', color: 'inherit' }}>
            {STATUS_LABEL[status]}
          </Typography>
        </Box>
      )}
    </Card>
  );
};
